import React, { useState, useEffect } from 'react';
import { Tabs, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ExistingVariationsTable from './ExistingVariationsTable';
import VariationForm from './VariationForm';
import { getProductVariationsByProductId } from '../../../core/api/products';

const { TabPane } = Tabs;

const ProductVariationsSection = ({ productId, uniqueId, refreshProduct }) => {
  const [activeKey, setActiveKey] = useState('1');
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [panes, setPanes] = useState([
    { key: '1', title: 'Existing Variations', closable: false },
  ]);

  useEffect(() => {
    if (productId) {
      fetchVariations();
    }
  }, [productId]);

  const fetchVariations = async () => {
    try {
      setLoading(true);
      const response = await getProductVariationsByProductId(productId);
      if (response && response.data) {
        setVariations(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch product variations');
      console.error('Error fetching variations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVariation = (variation) => {
    setSelectedVariation(variation);
    
    // Check if edit tab already exists
    const editTabKey = 'edit-variation';
    const existingTab = panes.find(pane => pane.key === editTabKey);
    
    if (!existingTab) {
      setPanes([
        ...panes,
        { 
          key: editTabKey, 
          title: 'Edit Variation', 
          closable: true 
        }
      ]);
    }
    
    setActiveKey(editTabKey);
  };

  const handleAddNewVariation = () => {
    setSelectedVariation(null);
    
    // Check if add tab already exists
    const addTabKey = 'add-variation';
    const existingTab = panes.find(pane => pane.key === addTabKey);
    
    if (!existingTab) {
      setPanes([
        ...panes,
        { 
          key: addTabKey, 
          title: 'Add New Variation', 
          closable: true 
        }
      ]);
    }
    
    setActiveKey(addTabKey);
  };

  const handleTabChange = (key) => {
    setActiveKey(key);
  };

  const handleTabEdit = (targetKey, action) => {
    if (action === 'remove') {
      const newPanes = panes.filter(pane => pane.key !== targetKey);
      setPanes(newPanes);
      setActiveKey('1'); // Go back to existing variations tab
    }
  };

  const handleVariationSaved = () => {
    fetchVariations();
    if (refreshProduct) {
      refreshProduct();
    }
    setActiveKey('1'); // Go back to existing variations tab
    
    // Remove the add/edit tab
    const newPanes = panes.filter(pane => 
      pane.key !== 'add-variation' && pane.key !== 'edit-variation'
    );
    setPanes(newPanes);
  };

  return (
    <div className="product-variations-section">
      <Tabs
        type="editable-card"
        onChange={handleTabChange}
        activeKey={activeKey}
        onEdit={handleTabEdit}
        hideAdd
        tabBarExtraContent={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddNewVariation}
          >
            Add New Variation
          </Button>
        }
      >
        <TabPane tab="Existing Variations" key="1" closable={false}>
          <ExistingVariationsTable 
            variations={variations} 
            loading={loading} 
            onEdit={handleEditVariation} 
            onRefresh={fetchVariations}
            productId={productId}
          />
        </TabPane>
        
        {panes.map(pane => {
          if (pane.key === '1') return null; // Skip the first tab as it's already defined
          
          if (pane.key === 'add-variation') {
            return (
              <TabPane tab={pane.title} key={pane.key} closable={pane.closable}>
                <VariationForm 
                  productId={productId}
                  onSuccess={handleVariationSaved}
                  onCancel={() => handleTabEdit(pane.key, 'remove')}
                  uniqueId={uniqueId}
                />
              </TabPane>
            );
          }
          
          if (pane.key === 'edit-variation' && selectedVariation) {
            return (
              <TabPane tab={pane.title} key={pane.key} closable={pane.closable}>
                <VariationForm 
                  productId={productId}
                  variation={selectedVariation}
                  onSuccess={handleVariationSaved}
                  onCancel={() => handleTabEdit(pane.key, 'remove')}
                  uniqueId={uniqueId}
                />
              </TabPane>
            );
          }
          
          return null;
        })}
      </Tabs>
    </div>
  );
};

export default ProductVariationsSection;
